import { MergeCheck } from 'src/types';
import { ForgeMergeCheckResult } from 'src/types/custom-merge-checks';

/**
 * Derive a key for the given ForgeMergeCheckResult. This is used where we need
 * an unique identifier for result items, e.g. in `MergeChecksCard` where
 * we render ForgeMergeCheckResultItems in a list. We can't use
 * ForgeMergeCheckResult.id since it isn't present for IMPENDING results.
 *
 * if a check has both pre- and on-merge triggers, the results would have the same check/config ID.
 * So we need to include the trigger in the key to differentiate them.
 */
export const makeCustomCheckKey = (result: ForgeMergeCheckResult): string =>
  `custom/${result.check.id}/${result.configUuid}/${result.trigger}`;

export const makeClassicCheckKey = (check: MergeCheck): string =>
  `classic/${check.key}`;

export const isForgeMergeCheckResult = (
  obj: unknown
): obj is ForgeMergeCheckResult => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'checkType' in obj &&
    'status' in obj
  );
};

export const isClassicMergeCheck = (obj: unknown): obj is MergeCheck => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'key' in obj &&
    'pass' in obj &&
    'name' in obj &&
    'allow_merge' in obj
  );
};

export const isMergeCheckFailedOrError = (
  mergeCheck: ForgeMergeCheckResult | MergeCheck
): boolean => {
  if (isForgeMergeCheckResult(mergeCheck)) {
    return mergeCheck.status === 'FAILED' || mergeCheck.status === 'ERROR';
  } else if (isClassicMergeCheck(mergeCheck)) {
    return !mergeCheck.pass;
  }
  return false;
};

export const isCustomCheckCompleted = (
  result: ForgeMergeCheckResult
): boolean => {
  return isMergeCheckFailedOrError(result) || result.status === 'SUCCESS';
};

// If checks have a SUCCESS or IMPENDING status then we can merge.
// Impending means on-merge checks are yet to run until the user tries to merge.
// We also ignore any checks that aren't required, as long as they aren't in progress/pending.
export const customCheckResultAllowsMerge = (
  result: ForgeMergeCheckResult
): boolean => {
  return (
    result.status === 'SUCCESS' ||
    result.status === 'IMPENDING' ||
    (result.enforcement === 'recommended' &&
      result.status !== 'IN-PROGRESS' &&
      result.status !== 'PENDING')
  );
};

/**
 * This helper looks at both `allow_merge` and `pass` to determine if a classic
 * check will allow the merge.
 *
 * `pass` is just whether or not the the check conditions have been met.
 *
 * `allow_merge` is a value calculated across *all* checks (i.e. all configured
 * classic checks for a given PR will have the same `allow_merge` value).
 * `allow_merge` is `true` in the following situations:
 *    - all configured merge checks for the PR have passed
 *    - the 'Prevent a merge with unresolved merge checks' on the branch
 *      restriction is disabled, or the workspace is not premium
 *
 * You can think of `allow_merge` as being the equivalent of
 * `ForgeMergeCheckResult.enforcement === 'recommended'` for custom merge
 * checks. The difference is that `allow_merge` is always `true` when all
 * classic checks have passed, regardless of whethe or not classic checks are
 * enforced. But in that case we don't care about whether or not the check is
 * being enforced since the merge will be allowed.
 */
export const classicCheckAllowsMerge = (check: MergeCheck): boolean => {
  return check.allow_merge || check.pass;
};

export const isCustomCheckRecommendedAndFailed = (
  result: ForgeMergeCheckResult
): boolean => {
  return (
    result.enforcement === 'recommended' && isMergeCheckFailedOrError(result)
  );
};

export const isCustomCheckRequiredAndFailed = (
  result: ForgeMergeCheckResult
): boolean => {
  return result.enforcement === 'required' && isMergeCheckFailedOrError(result);
};

export const isClassicCheckRecommendedAndFailed = (
  check: MergeCheck
): boolean => {
  return check.allow_merge && !check.pass;
};
